import { configureStore, combineReducers, type Action } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { authReducer, userSignedOut } from '@features/auth/slice';
import { authApi } from '@features/auth/api';
import { ordersApi } from '@features/orders/api';
import { shipmentsApi } from '@features/shipments/api';
import { historyApi } from '@features/history/api';
import { notesApi } from '@features/notes/api';
import {
  merchantApi,
  merchantTemplateApi,
  merchantAuthClientApi,
  merchantShippingMappingsApi,
} from '@features/merchant/api';
import { vendorApi } from '@features/vendor/api';
import { carrierApi } from '@features/carrier/api';
import { integrationApi } from '@features/integration/api';
import { filesApi } from '@features/files/api';
import { catalogApi } from '@features/catalog/api';
import { auditApi } from '@features/audit/api';
import { workflowApi } from '@features/workflow/api';
import { insightApi } from '@features/insight/api';
import { insightReducer } from '@features/insight/slice';
import { exportsApi } from '@features/exports/api';
import { onboardingApi } from '@features/onboarding/api';
import { routingApi } from '@features/routing/api';
import { inventoryApi } from '@features/inventory/api';
import { usersApi } from '@features/users/api';
import { webhooksApi } from '@features/webhooks/api';
import { skuMappingsApi } from '@features/skuMappings/api';
import { catalogSyncApi, productMatchingApi, productTypeFamiliesApi, searchSynonymsApi } from '@features/skuMappings/ProductMapping/api';
import { productMappingRunReducer } from '@features/skuMappings/ProductMapping/runSlice';
import { pricingApi } from '@features/pricing/api';
import { sseReducer } from '@features/sse/slice';
import { sseMiddleware } from '@features/sse/middleware';

const combinedReducer = combineReducers({
  auth: authReducer,
  sse: sseReducer,
  insightUI: insightReducer,
  productMappingRun: productMappingRunReducer,
  [authApi.reducerPath]: authApi.reducer,
  [ordersApi.reducerPath]: ordersApi.reducer,
  [shipmentsApi.reducerPath]: shipmentsApi.reducer,
  [historyApi.reducerPath]: historyApi.reducer,
  [notesApi.reducerPath]: notesApi.reducer,
  [merchantApi.reducerPath]: merchantApi.reducer,
  [merchantTemplateApi.reducerPath]: merchantTemplateApi.reducer,
  [merchantAuthClientApi.reducerPath]: merchantAuthClientApi.reducer,
  [merchantShippingMappingsApi.reducerPath]: merchantShippingMappingsApi.reducer,
  [vendorApi.reducerPath]: vendorApi.reducer,
  [carrierApi.reducerPath]: carrierApi.reducer,
  [integrationApi.reducerPath]: integrationApi.reducer,
  [filesApi.reducerPath]: filesApi.reducer,
  [catalogApi.reducerPath]: catalogApi.reducer,
  [auditApi.reducerPath]: auditApi.reducer,
  [workflowApi.reducerPath]: workflowApi.reducer,
  [insightApi.reducerPath]: insightApi.reducer,
  [onboardingApi.reducerPath]: onboardingApi.reducer,
  [routingApi.reducerPath]: routingApi.reducer,
  [exportsApi.reducerPath]: exportsApi.reducer,
  [inventoryApi.reducerPath]: inventoryApi.reducer,
  [usersApi.reducerPath]: usersApi.reducer,
  [webhooksApi.reducerPath]: webhooksApi.reducer,
  [skuMappingsApi.reducerPath]: skuMappingsApi.reducer,
  [productMatchingApi.reducerPath]: productMatchingApi.reducer,
  [catalogSyncApi.reducerPath]: catalogSyncApi.reducer,
  [searchSynonymsApi.reducerPath]: searchSynonymsApi.reducer,
  [productTypeFamiliesApi.reducerPath]: productTypeFamiliesApi.reducer,
  [pricingApi.reducerPath]: pricingApi.reducer,
});

/**
 * On sign-out, wipe the whole store — including every RTK Query cache — by
 * re-running the combined reducer with `undefined` state. Otherwise an
 * in-memory cache from the previous session (e.g. the super-admin orders list,
 * keyed only by query args, not by user) would be served to the next user who
 * logs in without a full page reload, leaking cross-tenant data until a manual
 * refresh. A full reset is equivalent to a fresh page load.
 */
const rootReducer: typeof combinedReducer = (state, action: Action) =>
  combinedReducer(action.type === userSignedOut.type ? undefined : state, action);

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      authApi.middleware,
      ordersApi.middleware,
      shipmentsApi.middleware,
      historyApi.middleware,
      notesApi.middleware,
      merchantApi.middleware,
      merchantTemplateApi.middleware,
      merchantAuthClientApi.middleware,
      merchantShippingMappingsApi.middleware,
      vendorApi.middleware,
      carrierApi.middleware,
      integrationApi.middleware,
      filesApi.middleware,
      catalogApi.middleware,
      auditApi.middleware,
      workflowApi.middleware,
      insightApi.middleware,
      onboardingApi.middleware,
      routingApi.middleware,
      exportsApi.middleware,
      inventoryApi.middleware,
      usersApi.middleware,
      webhooksApi.middleware,
      skuMappingsApi.middleware,
      productMatchingApi.middleware,
      catalogSyncApi.middleware,
      searchSynonymsApi.middleware,
      productTypeFamiliesApi.middleware,
      pricingApi.middleware,
      sseMiddleware,
    ),
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
